<script setup lang="ts">
  import { nextTick, ref } from "vue";
  import { useRouter } from "vue-router";
  import { DEFAULT_FILTERS, useReportStore } from "@/entities/report";
  import type { ReportFilters } from "@/entities/report";
  import { PAGES } from "@/entities/tech";
  import Step3Period, {
    type SelectedPeriod,
  } from "@/components/molecules/constructor/Step3Period.vue";
  import Step5Indicators from "@/components/molecules/constructor/Step5Indicators.vue";
  import { usePersistedSet } from "@/hooks/use-persisted-set";
  import Step1Attribution from "./components/Step1Attribution.vue";
  import Step2Providers from "./components/Step2Providers.vue";
  import Step4Breakdown from "./components/Step4Breakdown.vue";

  const router = useRouter();
  const reportStore = useReportStore();

  type StepInstance = { rootEl: HTMLElement | null };

  const currentStep = ref(1);
  const step1Ref = ref<StepInstance | null>(null);
  const step2Ref = ref<StepInstance | null>(null);
  const step3Ref = ref<StepInstance | null>(null);
  const step4Ref = ref<StepInstance | null>(null);
  const step5Ref = ref<StepInstance | null>(null);

  const selectedAttributionKey = ref<number>(0);
  const selectedProviders = ref(new Set<number>());
  const selectedPeriodKey = ref<SelectedPeriod>(11);
  const selectedDateFrom = ref<string | null>(null);
  const selectedDateTo = ref<string | null>(null);
  const selectedSectionKey = ref<number>(6);
  const selectedGroupByKey = ref<number | null>(null);
  // Persisted separately from the comparison wizard so each remembers its own
  // last indicator selection across visits.
  const selectedIndicators = usePersistedSet("create-report:step5-indicators");

  function scrollToStep(step: number) {
    const refs = [step1Ref, step2Ref, step3Ref, step4Ref, step5Ref];
    refs[step - 1]?.value?.rootEl?.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  async function reveal(step: number) {
    if (step > currentStep.value) currentStep.value = step;
    await nextTick();
    scrollToStep(step);
  }

  function submit() {
    const isFixedPeriod = selectedPeriodKey.value === "fixPeriod";
    const filters: ReportFilters = {
      ...DEFAULT_FILTERS,
      attribution: selectedAttributionKey.value,
      providers: Array.from(selectedProviders.value),
      period: selectedPeriodKey.value,
      dateFrom: isFixedPeriod ? selectedDateFrom.value : null,
      dateTo: isFixedPeriod ? selectedDateTo.value : null,
      section: selectedSectionKey.value,
      groupByNested: selectedGroupByKey.value,
      indicators: Array.from(selectedIndicators.value),
    };
    reportStore.setCurrentFilters(filters);
    void router.push({ name: PAGES.ReportByFilters });
  }
</script>

<template>
  <div :class="$style.base">
    <Step1Attribution ref="step1Ref" v-model="selectedAttributionKey" @next="reveal(2)" />
    <Step2Providers
      v-if="currentStep >= 2"
      ref="step2Ref"
      v-model="selectedProviders"
      @next="reveal(3)"
      @back="scrollToStep(1)"
    />
    <Step3Period
      v-if="currentStep >= 3"
      ref="step3Ref"
      v-model="selectedPeriodKey"
      v-model:date-from="selectedDateFrom"
      v-model:date-to="selectedDateTo"
      @next="reveal(4)"
      @back="scrollToStep(2)"
    />
    <Step4Breakdown
      v-if="currentStep >= 4"
      ref="step4Ref"
      v-model:entity="selectedSectionKey"
      v-model:group-by="selectedGroupByKey"
      @next="reveal(5)"
      @back="scrollToStep(3)"
    />
    <Step5Indicators
      v-if="currentStep >= 5"
      ref="step5Ref"
      v-model="selectedIndicators"
      @submit="submit"
      @back="scrollToStep(4)"
    />
  </div>
</template>

<style lang="scss" module>
  .base {
    width: 100%;
    height: 100%;
    max-height: 100%;
    padding: 0 20px;

    overflow-y: auto;
    scroll-snap-type: y mandatory;

    display: flex;
    flex-direction: column;
  }
</style>
